import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Text} from '@/components/ui/text/Text'
import dayjs, {formatLocaleDate} from '@/dayjs'
import {RenewProjectConfirmationFormSchema, RenewProjectFormValidationSchema} from '@/features/projects/formSchemas'
import {useStoreRenewTransformProject} from '@/features/projects/queries/useStoreRenewTransformProject'
import {assistiveRegimeToRehabCareCommitment} from '@/features/projects/utils'
import {QUERY_KEYS, queryClient} from '@/queryClient'
import {useTranslation} from '@/translations/i18n'
import {routes} from '@/utilities/constants/routes'
import {toast} from '@/utilities/toast/toast'
import {FormProvider, useForm, useFormContext, useWatch} from 'react-hook-form'
import {generatePath, useNavigate} from 'react-router-dom'
import {DialogHeading, DialogStore} from '@ariakit/react'
import {ConfirmationDialog} from '@/components/ui/confirmation-dialog-atoms/ConfirmationDialogAtoms'
import {zodResolver} from '@hookform/resolvers/zod'

interface RenewProjectConfirmationDialogProps {
    dialogStore: DialogStore
    projectId: string
}

export const RenewProjectConfirmationDialog = ({dialogStore, projectId}: RenewProjectConfirmationDialogProps) => {
    const {getValues} = useFormContext<RenewProjectFormValidationSchema>()
    const form = useForm<RenewProjectConfirmationFormSchema>({
        resolver: zodResolver(RenewProjectConfirmationFormSchema),
        defaultValues: getValues()
    })

    const patient = useWatch({control: form.control, name: 'patient'})

    const {t} = useTranslation()
    const navigate = useNavigate()

    const {mutate: renewTransformProject, isPending} = useStoreRenewTransformProject({
        projectId: projectId,
        options: {
            onSuccess: project => {
                void queryClient.cancelQueries({queryKey: [QUERY_KEYS.PROJECTS]})
                void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECTS]})
                toast.success(
                    <Flexbox direction="column">
                        <Text size="textSm" weight="500">
                            {t('projects:project_created')}
                        </Text>
                        {project.patient.fullName && (
                            <Text size="textSm" palette="neutral" shade="500">
                                {project.patient.fullName}
                            </Text>
                        )}
                    </Flexbox>
                )

                navigate(generatePath(routes.PROJECT_DETAILS_ROUTE.handle.path, {id: project.id}))
            },
            onError: error => {
                console.error('Renew project error: ', error)
                dialogStore.hide()
            }
        }
    })

    const handleConfirmSubmit = form.handleSubmit(formValues => {
        renewTransformProject({
            action: 'renew',
            type: 'continuous',
            sector: formValues.sector,
            assistiveRegime: formValues.assistiveRegime || null,
            startDate: formatLocaleDate(formValues.startDate, 'YYYY-MM-DD'),
            expirationDate: formatLocaleDate(formValues.expirationDate, 'YYYY-MM-DD'),
            durationDays: formValues.durationDays || dayjs(formValues.startDate).diff(formValues.expirationDate, 'day'),
            sender: formValues.sender,
            empty: false,
            specialistId: formValues.specialist.id,
            // This is sent to the backend just to allow the SIAR export, it is not a field of our form
            rehabilitativeAssistanceCommitment:
                formValues.sector == 'outpatient' && formValues.assistiveRegime
                    ? assistiveRegimeToRehabCareCommitment[formValues.assistiveRegime].toString()
                    : undefined
        })
    })

    return (
        <FormProvider {...form}>
            <ConfirmationDialog
                dialogStore={dialogStore}
                onConfirm={handleConfirmSubmit}
                isConfirmPending={isPending}
                headerSlot={
                    <Flexbox direction="column">
                        <DialogHeading>{t('projects:add_project_confirmation_modal_title')}</DialogHeading>
                        <Text palette="neutral" shade="500" size="textSm">
                            {t('commons:patient')}: {patient.fullName}
                        </Text>
                    </Flexbox>
                }
            />
        </FormProvider>
    )
}
